import type { ApolloError, FetchResult, MutationFunctionOptions } from '@apollo/client'
import { toast } from 'sonner'

interface MutationWithToastOptions<TData, TVariables> {
  mutationFn: (options?: MutationFunctionOptions<TData, TVariables>) => Promise<FetchResult<TData>>
  loadingMessage?: string
  successMessage?: string
  onError?: (error: ApolloError) => void
}

interface ExecuteMutationOptions<TData, TVariables> extends MutationFunctionOptions<TData, TVariables> {
  loadingMessage?: string
  successMessage?: string
}

/**
 * Custom hook that wraps a GraphQL mutation with toast notifications
 * Handles loading, success, and error states with toast messages
 *
 * @param mutationFn - The mutation function from useMutation hook
 * @param loadingMessage - Optional default loading message (default: 'Loading...', can be overridden at execution time)
 * @param successMessage - Optional default success message (default: 'Success!', can be overridden at execution time)
 * @param onError - Optional custom error handler
 * @returns A wrapped mutation function that handles toast notifications and accepts optional dynamic messages
 *
 * @example
 * const [updateStatus] = useMutation(UPDATE_STATUS)
 * const executeMutation = useMutationWithToast({
 *   mutationFn: updateStatus,
 *   loadingMessage: 'Updating status...',
 *   successMessage: 'Status updated!'
 * })
 * // Use default messages
 * await executeMutation({ variables: { id: '123', status: 'active' } })
 * // Override messages at execution time
 * await executeMutation({
 *   variables: { id: '456', status: 'inactive' },
 *   loadingMessage: 'Custom loading...',
 *   successMessage: 'Custom success!'
 * })
 */
export const useMutationWithToast = <TData = unknown, TVariables = unknown>(
  options: MutationWithToastOptions<TData, TVariables>
) => {
  const {
    mutationFn,
    loadingMessage: defaultLoadingMessage = 'Loading...',
    successMessage: defaultSuccessMessage = 'Success!',
    onError
  } = options

  return async (mutationOptions?: ExecuteMutationOptions<TData, TVariables>) => {
    const { loadingMessage, successMessage, ...apolloOptions } = mutationOptions || {}

    const finalLoadingMessage = loadingMessage || defaultLoadingMessage
    const finalSuccessMessage = successMessage || defaultSuccessMessage

    toast.loading(finalLoadingMessage)
    try {
      const result = await mutationFn(apolloOptions)
      toast.success(finalSuccessMessage)
      return result
    } catch (error) {
      const errorMessage = (error as Error).message
      toast.error(errorMessage)
      if (onError) {
        onError(error as ApolloError)
      }
      throw error
    } finally {
      toast.dismiss()
    }
  }
}
